import { useCallback, useMemo, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { parse } from 'papaparse';
import { UploadSimple } from '@phosphor-icons/react';
import './stream.css';

function parseNumber(value) {
  if (!value) return 0;
  if (typeof value === 'string') {
    return parseFloat(value.replace(/[^0-9.-]+/g, '')) || 0;
  }
  return Number(value) || 0;
}

function isUKAddress(address) {
  return address && address.toString().trim().toLowerCase().endsWith('gb');
}

function isGlobalSchemeProduct(description) {
  return description && (description.includes('#XZ~') || description.includes('#XY~'));
}

function calculateWhatnotFees(salePrice) {
  const commission = salePrice * 0.0667;
  const processingFee = salePrice * 0.0242 + 0.25;
  return commission + processingFee;
}

function extractProductQuantity(name) {
  if (!name) return { quantity: 1, baseName: '' };

  const quantityMatch = name.match(/^(\d+)x\s+(.+)$/i);
  if (quantityMatch) {
    const quantity = parseInt(quantityMatch[1], 10);
    const baseName = quantityMatch[2].trim();

    if (baseName.includes('Ruler of The Black Flame')) {
      return {
        quantity: 1,
        baseName: name,
        packSize: quantity,
      };
    }

    return { quantity, baseName };
  }

  return { quantity: 1, baseName: name };
}

function normalizeProductName(name) {
  if (!name) return '';

  const { baseName } = extractProductQuantity(name);

  return baseName
    .replace(/\s+#\d+$/i, '')
    .replace(/\s*\(\d+\)$/i, '')
    .replace(/\s*-\s*\d+$/i, '')
    .trim();
}

function processWhatnotCSV(records) {
  const validRecords = records.filter((record) => {
    return Object.keys(record).length > 3 && Object.values(record).some((value) => value && value.toString().trim() !== '');
  });

  if (validRecords.length === 0) {
    throw new Error('No valid records found in the CSV file');
  }

  const firstRecord = validRecords[0];

  // Detect key fields from Whatnot CSV
  // Normalise underscores → spaces so 'order_id' matches 'order id' etc.
  const normKey = (k) => k.toLowerCase().replace(/_/g, " ");

  const saleIdField = Object.keys(firstRecord).find(
    (key) => normKey(key).includes('order id') || normKey(key).includes('sale id') || key.toLowerCase() === 'id'
  );

  const productNameField = Object.keys(firstRecord).find(
    (key) => normKey(key).includes('product name') || normKey(key).includes('item title') || key.toLowerCase() === 'title'
  );

  const priceField = Object.keys(firstRecord).find(
    (key) =>
      normKey(key) === 'original item price' ||
      normKey(key).includes('sold price') ||
      normKey(key).includes('sale price') ||
      (normKey(key).includes('price') && !normKey(key).includes('coupon'))
  );

  const descriptionField = Object.keys(firstRecord).find(
    (key) => normKey(key).includes('description') || normKey(key).includes('notes')
  );

  const quantityField = Object.keys(firstRecord).find(
    (key) => normKey(key).includes('quantity') || normKey(key).includes('qty')
  );

  const addressField = Object.keys(firstRecord).find(
    (key) => normKey(key).includes('shipping address') || normKey(key).includes('address')
  );

  const statusField = Object.keys(firstRecord).find(
    (key) =>
      normKey(key).includes('cancelled or failed') ||
      normKey(key).includes('cancelled') ||
      normKey(key).includes('status') ||
      normKey(key).includes('failed')
  );

  const costField = Object.keys(firstRecord).find(
    (key) => normKey(key) === 'total cost' || normKey(key) === 'cost per item'
  );

  const shipmentManifestField = Object.keys(firstRecord).find(
    (key) =>
      normKey(key).includes('shipment manifest') ||
      normKey(key) === 'manifest' ||
      normKey(key).includes('shipping manifest')
  );

  const skuField = Object.keys(firstRecord).find(
    (key) => normKey(key) === 'sku' || normKey(key).includes('item number') || normKey(key).includes('product code')
  );

  const timestampField = Object.keys(firstRecord).find(
    (key) =>
      normKey(key) === 'placed at' ||
      normKey(key).includes('order date') ||
      normKey(key).includes('order placed') ||
      normKey(key).includes('timestamp') ||
      normKey(key).includes('placed at')
  );

  if (!saleIdField || !productNameField || !priceField) {
    throw new Error('Could not identify required fields in the CSV (Order ID, Product Name, Price)');
  }

  const validSalesRecords = [];
  const cancelledSalesRecords = [];
  let cancelledCount = 0;
  const allSalesDebug = [];

  validRecords.forEach((record) => {
    const saleId = record[saleIdField];
    const productName = record[productNameField] || 'Unknown Product';
    const priceStr = record[priceField] || '0';
    const price = parseNumber(priceStr);
    const qtyStr = record[quantityField] || '1';
    const qty = parseNumber(qtyStr);

    let isCancelled = false;
    let cancelReason = '';

    if (statusField && record[statusField]) {
      const status = record[statusField].toString().toLowerCase();
      if (status.includes('cancel')) {
        isCancelled = true;
        cancelReason = 'status field contains "cancel"';
      } else if (status.includes('fail')) {
        isCancelled = true;
        cancelReason = 'status field contains "fail"';
      }
    }

    const cancelField = Object.keys(record).find(
      (key) => (key.toLowerCase().includes('cancel') || key.toLowerCase().includes('fail')) && key.toLowerCase() !== statusField?.toLowerCase()
    );

    if (cancelField && record[cancelField] && record[cancelField].toString().toLowerCase() !== 'false') {
      isCancelled = true;
      cancelReason = `${cancelField} field has value "${record[cancelField]}"`;
    }

    if (shipmentManifestField && (!record[shipmentManifestField] || record[shipmentManifestField].toString().trim() === '')) {
      isCancelled = true;
      cancelReason = 'empty shipment manifest field';
      console.log(`Excluded order with empty shipment manifest: ${saleId} - ${productName} - Price: £${price}`);
    }

    allSalesDebug.push({
      saleId,
      productName,
      price,
      qty,
      isCancelled,
      cancelReason,
      revenue: price * qty,
      originalFields: {
        price: priceStr,
        qty: qtyStr,
        status: statusField ? record[statusField] : 'no status field',
        shipmentManifest: shipmentManifestField ? record[shipmentManifestField] : 'no manifest field',
      },
    });

    if (isCancelled) {
      cancelledCount += 1;
      cancelledSalesRecords.push({ ...record, cancelReason });
    } else {
      validSalesRecords.push(record);
    }
  });

  const productSummary = {};
  allSalesDebug.forEach((sale) => {
    if (!productSummary[sale.productName]) {
      productSummary[sale.productName] = {
        totalSales: 0,
        cancelledSales: 0,
        validSales: 0,
        totalRevenue: 0,
        cancelledRevenue: 0,
        validRevenue: 0,
        items: [],
      };
    }

    const summary = productSummary[sale.productName];
    summary.totalSales += 1;
    summary.totalRevenue += sale.revenue;

    if (sale.isCancelled) {
      summary.cancelledSales += 1;
      summary.cancelledRevenue += sale.revenue;
    } else {
      summary.validSales += 1;
      summary.validRevenue += sale.revenue;
    }

    summary.items.push(sale);
  });

  console.log('======= PRODUCT REVENUE SUMMARY =======');

  const missingManifestCount = allSalesDebug.filter(
    (sale) => sale.isCancelled && sale.cancelReason === 'empty shipment manifest field'
  ).length;

  const missingManifestRevenue = allSalesDebug
    .filter((sale) => sale.isCancelled && sale.cancelReason === 'empty shipment manifest field')
    .reduce((sum, sale) => sum + sale.revenue, 0);

  console.log(`Orders excluded due to empty shipment manifest: ${missingManifestCount} (£${missingManifestRevenue.toFixed(2)} total)`);

  let totalValidRevenue = 0;
  let totalCancelledRevenue = 0;

  Object.entries(productSummary)
    .sort(([, a], [, b]) => b.totalRevenue - a.totalRevenue)
    .forEach(([productName, summary]) => {
      totalValidRevenue += summary.validRevenue;
      totalCancelledRevenue += summary.cancelledRevenue;

      if (summary.totalRevenue > 100 || summary.cancelledSales > 0) {
        console.log(`${productName}:
          • Total Revenue: £${summary.totalRevenue.toFixed(2)} (${summary.totalSales} sales)
          • Valid Revenue: £${summary.validRevenue.toFixed(2)} (${summary.validSales} sales)
          • Cancelled Revenue: £${summary.cancelledRevenue.toFixed(2)} (${summary.cancelledSales} sales)
        `);
      }
    });

  console.log(`
    TOTAL VALID REVENUE: £${totalValidRevenue.toFixed(2)}
    TOTAL CANCELLED REVENUE: £${totalCancelledRevenue.toFixed(2)}
    EXPECTED WHATNOT REPORTED REVENUE: £${totalValidRevenue.toFixed(2)}
    DIFFERENCE FROM £4150.60: £${(totalValidRevenue - 4150.60).toFixed(2)}
  `);

  const largeOrders = allSalesDebug
    .filter((sale) => !sale.isCancelled && sale.revenue > 50)
    .sort((a, b) => b.revenue - a.revenue);

  console.log('TOP 10 LARGEST VALID ORDERS:');
  largeOrders.slice(0, 10).forEach((order) => {
    console.log(`• ${order.productName}: £${order.revenue.toFixed(2)} (${order.qty} @ £${order.price.toFixed(2)})`);
  });

  const bundledOrders = allSalesDebug.filter(
    (sale) => !sale.isCancelled && sale.productName.toLowerCase().includes('bundle')
  );

  if (bundledOrders.length > 0) {
    console.log('BUNDLED ORDERS (potential source of discrepancy):');
    bundledOrders.forEach((order) => {
      console.log(`• ${order.productName}: £${order.revenue.toFixed(2)}`);
    });
  }

  const suspiciousSales = allSalesDebug.filter((sale) => {
    return (
      !sale.isCancelled &&
      (sale.qty > 100 || sale.revenue > 500 || sale.productName.toLowerCase().includes('test'))
    );
  });

  if (suspiciousSales.length > 0) {
    console.log('SUSPICIOUS SALES (may be excluded by Whatnot):');
    suspiciousSales.forEach((sale) => {
      console.log(`• ${sale.productName}: £${sale.revenue.toFixed(2)} (qty: ${sale.qty})`);
    });
    console.log(
      `Total suspicious sales revenue: £${suspiciousSales.reduce((sum, sale) => sum + sale.revenue, 0).toFixed(2)}`
    );
  }

  let totalRevenue = 0;
  let totalCost = 0;
  let totalFees = 0;
  let totalStandardVAT = 0;
  let totalGlobalVAT = 0;
  let ukSalesCount = 0;
  let globalSchemeCount = 0;

  const productMap = {};
  const globalSchemeItems = [];
  const allGlobalSchemeItems = [];

  validSalesRecords.forEach((record) => {
    const saleId = record[saleIdField];
    const productName = record[productNameField] || 'Unknown Product';
    const salePrice = parseNumber(record[priceField] || '0');
    const description = record[descriptionField] || '';
    let recordQuantity = parseNumber(record[quantityField] || '1');
    const address = record[addressField] || '';

    if (recordQuantity > 50) {
      console.warn(`Extremely high quantity detected: ${productName} x${recordQuantity}`);
      recordQuantity = 1;
    }

    const isSuspicious =
      recordQuantity > 100 || productName.toLowerCase().includes('test');

    if (isSuspicious) {
      console.log(`Skipping suspicious sale: ${productName} - £${salePrice * recordQuantity}`);
      return;
    }

    const isUK = isUKAddress(address);
    if (isUK) {
      ukSalesCount += 1;
    }

    const extractedQuantity = extractProductQuantity(productName);
    const packQuantity = extractedQuantity.quantity;
    const baseName = extractedQuantity.baseName;
    const packSize = extractedQuantity.packSize;

    let totalItemQuantity = packSize ? packSize * recordQuantity : packQuantity * recordQuantity;

    let costPrice = 0;
    let hasCostPrice = false;

    if (costField && record[costField] && record[costField].toString().trim() !== '') {
      costPrice = parseNumber(record[costField]);
      hasCostPrice = true;
    }

    const isGlobalScheme = isGlobalSchemeProduct(description);
    if (isGlobalScheme) {
      globalSchemeCount += 1;
    }

    const whatnotFees = calculateWhatnotFees(salePrice);

    let standardVAT = 0;
    let globalVAT = 0;

    if (isGlobalScheme) {
      const margin = salePrice - costPrice;
      globalVAT = Math.max(0, margin * 0.2);

      const globalSchemeItem = {
        saleId,
        productName,
        description,
        salePrice,
        costPrice,
        margin: salePrice - costPrice,
        vat: globalVAT,
        fees: whatnotFees,
        quantity: recordQuantity,
        packQuantity,
        totalItems: totalItemQuantity,
        isUK,
        sku: skuField && record[skuField] ? record[skuField] : '',
        timestamp: record[timestampField] ? record[timestampField] : '',
      };

      if (timestampField && record[timestampField]) {
        console.log(`Found timestamp for item ${saleId}: ${record[timestampField]}`);
      } else {
        console.warn(`No timestamp field found for item ${saleId}. Available fields:`, Object.keys(record));
      }

      allGlobalSchemeItems.push(globalSchemeItem);

      if (isUK) {
        globalSchemeItems.push({
          ...globalSchemeItem,
          address,
        });
      }
    } else {
      standardVAT = salePrice * 0.2;
    }

    const totalVAT = standardVAT + globalVAT;
    let netProfit = 0;
    if (hasCostPrice) {
      netProfit = salePrice - costPrice - whatnotFees - totalVAT;
    }

    totalRevenue += salePrice * recordQuantity;
    if (hasCostPrice) {
      totalCost += costPrice * recordQuantity;
    }
    totalFees += whatnotFees * recordQuantity;
    totalStandardVAT += standardVAT * recordQuantity;
    totalGlobalVAT += globalVAT * recordQuantity;

    const normalizedName = normalizeProductName(baseName || productName);

    if (!productMap[normalizedName]) {
      productMap[normalizedName] = {
        name: normalizedName,
        sku: skuField && record[skuField] ? record[skuField] : '',
        originalVariants: [productName],
        quantity: 0,
        totalItems: 0,
        totalRevenue: 0,
        totalCost: 0,
        totalFees: 0,
        totalVAT: 0,
        netProfit: 0,
        profitMargin: null,
        sales: [],
        isGlobalScheme,
      };
    } else if (!productMap[normalizedName].originalVariants.includes(productName)) {
      productMap[normalizedName].originalVariants.push(productName);
    }

    productMap[normalizedName].quantity += recordQuantity;
    productMap[normalizedName].totalItems += totalItemQuantity;
    productMap[normalizedName].totalRevenue += salePrice * recordQuantity;
    if (hasCostPrice) {
      productMap[normalizedName].totalCost += costPrice * recordQuantity;
    }
    productMap[normalizedName].totalFees += whatnotFees * recordQuantity;
    productMap[normalizedName].totalVAT += totalVAT * recordQuantity;

    productMap[normalizedName].sales.push({
      saleId,
      productName,
      salePrice,
      costPrice,
      whatnotFees,
      standardVAT,
      globalVAT,
      netProfit,
      quantity: recordQuantity,
      packQuantity,
      totalItems: totalItemQuantity,
      isGlobalScheme,
      hasCostPrice,
    });
  });

  const allProducts = Object.values(productMap);

  allProducts.forEach((product) => {
    let productNetProfit = 0;
    let productProfitMargin = null;

    const salesWithCostPrice = product.sales.filter((sale) => sale.hasCostPrice);

    if (salesWithCostPrice.length > 0) {
      productNetProfit = salesWithCostPrice.reduce((total, sale) => total + sale.netProfit * sale.quantity, 0);

      const revenueWithCost = salesWithCostPrice.reduce((total, sale) => total + sale.salePrice * sale.quantity, 0);

      if (revenueWithCost > 0) {
        productProfitMargin = (productNetProfit / revenueWithCost) * 100;
      }
    }

    product.netProfit = productNetProfit;
    product.profitMargin = productProfitMargin;
    product.variantCount = product.originalVariants.length;
    product.hasCostPrice = salesWithCostPrice.length > 0;
  });

  const totalVAT = totalStandardVAT + totalGlobalVAT;
  const productsWithCost = allProducts.filter((product) => product.hasCostPrice);
  const revenueWithCost = productsWithCost.reduce((total, product) => total + product.totalRevenue, 0);
  const feesWithCost = productsWithCost.reduce((total, product) => total + product.totalFees, 0);
  const vatWithCost = productsWithCost.reduce((total, product) => total + product.totalVAT, 0);
  const grossProfit = revenueWithCost - totalCost;
  const netProfit = revenueWithCost - totalCost - feesWithCost - vatWithCost;
  const profitMargin = revenueWithCost > 0 ? (netProfit / revenueWithCost) * 100 : 0;

  console.log(`FINAL CALCULATED VALUES:
    Total Revenue: £${totalRevenue.toFixed(2)}
    Revenue (with cost): £${revenueWithCost.toFixed(2)}
    Total Cost: £${totalCost.toFixed(2)}
    Gross Profit (items with cost): £${grossProfit.toFixed(2)}
    Net Profit (items with cost): £${netProfit.toFixed(2)}
    Profit Margin: ${profitMargin.toFixed(1)}%
  `);

  console.log(`Consistency check:
    - Direct sum: £${totalValidRevenue.toFixed(2)}
    - Final calculation: £${totalRevenue.toFixed(2)}
    - Difference: £${(totalValidRevenue - totalRevenue).toFixed(2)}
  `);

  return {
    processedData: {
      totalRevenue,
      totalCost,
      totalFees,
      totalStandardVAT,
      totalGlobalVAT,
      totalVAT,
      grossProfit,
      netProfit,
      profitMargin,
      revenueWithCost,
      feesWithCost,
      vatWithCost,
      products: allProducts,
      globalSchemeItems,
      allGlobalSchemeItems,
      stats: {
        validSales: validSalesRecords.length,
        cancelledSales: cancelledCount,
        ukSales: ukSalesCount,
        globalSchemeSales: globalSchemeCount,
        totalProducts: allProducts.length,
        ukGlobalSchemeItems: globalSchemeItems.length,
        allGlobalSchemeItems: allGlobalSchemeItems.length,
      },
    },
    rowCount: validRecords.length,
  };
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function CsvUploader({
  onProcessed,
  onError,
  onUploadStateChange,
  uploadedFileName,
  uploadedRowCount,
  hasStreamData,
}) {
  const [isUploading, setIsUploading] = useState(false);

  const startUpload = useCallback(() => {
    setIsUploading(true);
    onUploadStateChange?.(true);
    onError?.(null);
  }, [onError, onUploadStateChange]);

  const finishUpload = useCallback(() => {
    setIsUploading(false);
    onUploadStateChange?.(false);
  }, [onUploadStateChange]);

  const handleFile = useCallback(
    async (file) => {
      if (!file) return;

      startUpload();

      try {
        const csvContent = await file.text();

        parse(csvContent, {
          header: true,
          skipEmptyLines: true,
          complete: (results) => {
            try {
              if (results.errors.length > 0) {
                throw new Error(`Error parsing CSV: ${results.errors[0].message}`);
              }

              const { processedData, rowCount } = processWhatnotCSV(results.data);
              onProcessed?.({
                processedData,
                csvContent,
                fileName: file.name,
                rowCount,
              });
              finishUpload();
            } catch (error) {
              console.error('CSV processing error:', error);
              onError?.(error.message);
              finishUpload();
            }
          },
          error: (error) => {
            console.error('CSV parsing error:', error);
            onError?.(`Failed to parse CSV: ${error.message}`);
            finishUpload();
          },
        });
      } catch (error) {
        console.error('Upload error:', error);
        onError?.(`Error uploading file: ${error.message}`);
        finishUpload();
      }
    },
    [finishUpload, onError, onProcessed, startUpload]
  );

  const onDrop = useCallback(
    (acceptedFiles) => {
      if (!acceptedFiles.length) return;
      handleFile(acceptedFiles[0]);
    },
    [handleFile]
  );

  const { getRootProps, getInputProps, open, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'text/csv': ['.csv'],
    },
    multiple: false,
    noClick: true,
  });

  const statusLabel = useMemo(() => {
    if (!uploadedFileName) return 'No CSV uploaded yet';
    return `${uploadedFileName} • ${uploadedRowCount || 0} rows`;
  }, [uploadedFileName, uploadedRowCount]);

  return (
    <section className="rc-panel srv-upload">
      <div className="srv-upload__head">
        <div className="srv-heading">
          <p className="rc-eyebrow">
            CSV Intake
          </p>
          <h2 className="srv-title">
            Upload Whatnot Stream CSV
          </h2>
          <p className="srv-sub">
            Drag in the export, parse it client-side, and keep the existing revenue and VAT calculations intact.
          </p>
        </div>
        <button className={hasStreamData ? 'rc-btn' : 'rc-btn rc-btn--primary'} onClick={open}>
          {isUploading ? 'Uploading…' : hasStreamData ? 'Replace CSV' : 'Choose CSV'}
        </button>
      </div>

      <div
        {...getRootProps()}
        className="srv-drop"
        data-active={isDragActive ? 'true' : undefined}
      >
        <input {...getInputProps()} />
        <span className="srv-drop__icon">
          {isUploading ? <span className="rc-loader"><span /><span /><span /></span> : <UploadSimple size={20} aria-hidden="true" />}
        </span>
        <p className="srv-drop__title">
          {isDragActive ? 'Drop the stream CSV here' : 'Drop CSV here or use the upload button'}
        </p>
        <p className="srv-drop__status">{statusLabel}</p>
      </div>

      {isUploading && (
        <div className="rc-alert rc-alert--info">
          Parsing CSV and rebuilding stream metrics locally…
        </div>
      )}
    </section>
  );
}
